import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import {
  LayoutDashboard,
  Users,
  Map,
  ClipboardList,
  BarChart3,
  CreditCard,
  ScrollText,
  LogOut,
  Menu,
  X,
  FileSpreadsheet,
} from "lucide-react";

const ITEMS = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users & Approvals", icon: Users },
  { to: "/admin/wards", label: "Wards & Booths", icon: Map },
  { to: "/admin/form-builder", label: "Form Builder", icon: ClipboardList },
  { to: "/admin/submissions", label: "Submissions", icon: FileSpreadsheet },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/monetization", label: "Monetization", icon: CreditCard },
  { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const SideContent = (
    <>
      <div className="px-5 py-5 border-b border-slate-800">
        <Brand invert />
        <div className="mt-3 text-xs font-mono uppercase tracking-widest text-slate-500">
          Super Admin Console
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {ITEMS.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? "bg-blue-700 text-white"
                  : "text-slate-300 hover:bg-slate-800 hover:text-white"
              }`
            }
          >
            <it.icon size={16} />
            <span>{it.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-slate-800">
        <div className="px-3 mb-2 text-xs text-slate-400">
          <div className="truncate">{user?.email}</div>
          <div className="font-mono text-[10px] tracking-widest uppercase mt-0.5">
            {user?.role}
          </div>
        </div>
        <button
          onClick={async () => {
            await logout();
            nav("/login");
          }}
          data-testid="admin-logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-red-600 hover:text-white transition-colors"
        >
          <LogOut size={16} /> Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden lg:flex w-64 bg-slate-900 text-white flex-col fixed h-full">
        {SideContent}
      </aside>
      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-slate-900/60"
            onClick={() => setOpen(false)}
          />
          <aside className="relative w-64 bg-slate-900 text-white flex flex-col">
            {SideContent}
          </aside>
        </div>
      )}
      <div className="flex-1 lg:ml-64 flex flex-col">
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setOpen(true)}
            data-testid="admin-menu-toggle"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          <Brand />
          <div className="w-5" />
        </header>
        <main className="flex-1 px-4 md:px-8 py-6 md:py-10 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
